/**
 * Round a price value to 2 decimal places.
 * @param {number|string} val - Value to round
 * @returns {number}
 */
export const roundPrice = (val) => Number(Number(val ?? 0).toFixed(2));

const ROOM_SIZES = [
  { key: "quad", size: 4 },
  { key: "triple", size: 3 },
  { key: "double", size: 2 },
  { key: "single", size: 1 },
];

/**
 * Check if price object uses room-type structure (single, double, triple, quad)
 * @param {Object} priceObj - Deal price object
 * @returns {boolean}
 */
export const hasRoomTypePricing = (priceObj) =>
  priceObj &&
  typeof priceObj === "object" &&
  (priceObj.single || priceObj.double || priceObj.triple || priceObj.quad);

/**
 * Get price amount for a room type in given currency
 * @param {Object} priceObj - Deal price object (room type structure)
 * @param {string} roomType - 'single'|'double'|'triple'|'quad'
 * @param {Object} currencyContext - { currency, sign }
 * @returns {number}
 */
export const getPriceForRoomType = (priceObj, roomType, currencyContext) => {
  const roomPrices = priceObj?.[roomType];
  if (!roomPrices || typeof roomPrices !== "object") return 0;
  const currencyCode = (currencyContext?.currency || "EUR").toUpperCase();
  const priceMap = {
    USD: roomPrices.usd,
    EUR: roomPrices.eur,
    ANG: roomPrices.ang,
    SRD: roomPrices.srd,
  };
  const amount =
    priceMap[currencyCode] ??
    roomPrices.eur ??
    roomPrices.usd ??
    roomPrices.ang ??
    roomPrices.srd ??
    0;
  return roundPrice(amount);
};

/**
 * Get cheapest price across all room types for a deal (for "From" display on cards)
 * @param {Object} priceObj - Deal price object
 * @param {Object} currencyContext - { currency, sign }
 * @returns {{ amount: number, sign: string, currencyCode: string, formatted: string }}
 */
export const getCheapestDealPrice = (priceObj, currencyContext) => {
  if (!priceObj || typeof priceObj !== "object") {
    return { amount: 0, sign: " € ", currencyCode: "EUR", formatted: "0" };
  }

  const currencyCode = (currencyContext?.currency || "EUR").toUpperCase();
  const sign = currencyContext?.sign ?? " € ";

  if (hasRoomTypePricing(priceObj)) {
    let cheapest = Infinity;
    for (const { key } of ROOM_SIZES) {
      const amt = getPriceForRoomType(priceObj, key, currencyContext);
      if (amt > 0 && amt < cheapest) cheapest = amt;
    }
    const amount = cheapest === Infinity ? 0 : cheapest;
    const formatted =
      amount % 1 === 0
        ? amount.toLocaleString()
        : amount.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
    return { amount, sign, currencyCode, formatted };
  }

  return getPackageDealPrice(priceObj, currencyContext);
};

/**
 * Get room types that have pricing > 0 in the deal.
 * @param {Object} priceObj - Deal price object
 * @param {Object} currencyContext - { currency, sign }
 * @returns {string[]} e.g. ['single', 'double']
 */
export const getRoomTypesWithPricing = (priceObj, currencyContext) => {
  if (!hasRoomTypePricing(priceObj) || !currencyContext) return [];
  return ROOM_SIZES.filter(({ key }) => getPriceForRoomType(priceObj, key, currencyContext) > 0).map(
    (r) => r.key
  );
};

/**
 * Generate all room combinations for N people.
 * When priceObj and currencyContext are provided, only includes room types that have pricing > 0.
 * @param {number} numPeople
 * @param {Object} [priceObj] - Optional deal price object
 * @param {Object} [currencyContext] - Optional { currency, sign }
 * @returns {Array<{ rooms: Array<{type: string, count: number}>, label: string }>}
 */
export const getRoomCombinations = (numPeople, priceObj, currencyContext) => {
  if (numPeople < 1 || numPeople > 20) return [];
  const combinations = [];
  const roomTypesWithPrice =
    priceObj && currencyContext ? getRoomTypesWithPricing(priceObj, currencyContext) : ["quad", "triple", "double", "single"];

  const hasPricing = (type) => roomTypesWithPrice.includes(type);

  const formatLabel = (rooms) => {
    const parts = rooms
      .filter((r) => r.count > 0)
      .map((r) => {
        const type =
          r.type === "single"
            ? "Single"
            : r.type === "double"
            ? "Double"
            : r.type === "triple"
            ? "Triple"
            : "Quad";
        return r.count > 1 ? `${r.count}× ${type}` : `1× ${type}`;
      });
    return parts.join(" + ");
  };

  const maxQuad = hasPricing("quad") ? Math.floor(numPeople / 4) : 0;
  const maxTriple = hasPricing("triple") ? Math.floor(numPeople / 3) : 0;
  const maxDouble = hasPricing("double") ? Math.floor(numPeople / 2) : 0;

  for (let q = 0; q <= maxQuad; q++) {
    const afterQuad = numPeople - 4 * q;
    for (let t = 0; t <= Math.min(maxTriple, Math.floor(afterQuad / 3)); t++) {
      const afterTriple = afterQuad - 3 * t;
      for (let d = 0; d <= Math.min(maxDouble, Math.floor(afterTriple / 2)); d++) {
        const singles = afterTriple - 2 * d;
        if (singles >= 0 && (hasPricing("single") || singles === 0)) {
          const rooms = [
            hasPricing("quad") && { type: "quad", count: q },
            hasPricing("triple") && { type: "triple", count: t },
            hasPricing("double") && { type: "double", count: d },
            hasPricing("single") && { type: "single", count: singles },
          ]
            .filter(Boolean)
            .filter((r) => r.count > 0);
          if (rooms.length > 0) {
            combinations.push({
              rooms,
              label: formatLabel(rooms),
            });
          }
        }
      }
    }
  }

  return combinations;
};

const ROOM_OCCUPANCY = { single: 1, double: 2, triple: 3, quad: 4 };

/**
 * Calculate total price for a room combination.
 * Pricing is per person: total = sum(pricePerPerson × occupancy × roomCount)
 * @param {Object} priceObj - Deal price object (room type structure)
 * @param {Array<{type: string, count: number}>} rooms - Room combination
 * @param {Object} currencyContext - { currency }
 * @param {boolean} [perPerson=true] - If true, price × occupants per room
 * @returns {number}
 */
export const getCombinationPrice = (priceObj, rooms, currencyContext, perPerson = true) => {
  if (!hasRoomTypePricing(priceObj) || !rooms?.length) return 0;
  let total = 0;
  for (const { type, count } of rooms) {
    const pricePerPerson = getPriceForRoomType(priceObj, type, currencyContext);
    const occupants = ROOM_OCCUPANCY[type] || 1;
    total += perPerson ? pricePerPerson * occupants * count : pricePerPerson * count;
  }
  return roundPrice(total);
};

/**
 * Get price breakdown for a room combination (per-person pricing)
 * @returns {{ total: number, breakdown: Array<{type: string, count: number, pricePerPerson: number, occupants: number, lineTotal: number, label: string }> }}
 */
export const getCombinationBreakdown = (priceObj, rooms, currencyContext) => {
  if (!hasRoomTypePricing(priceObj) || !rooms?.length) {
    return { total: 0, breakdown: [] };
  }
  const breakdown = [];
  let total = 0;
  const typeLabels = { single: "Single", double: "Double", triple: "Triple", quad: "Quad" };
  for (const { type, count } of rooms) {
    if (count <= 0) continue;
    const pricePerPerson = getPriceForRoomType(priceObj, type, currencyContext);
    if (pricePerPerson <= 0) continue;
    const occupants = ROOM_OCCUPANCY[type] || 1;
    const lineTotal = roundPrice(pricePerPerson * occupants * count);
    total += lineTotal;
    breakdown.push({
      type,
      count,
      pricePerPerson,
      occupants,
      lineTotal,
      label: `${count > 1 ? count + "× " : ""}${typeLabels[type]}`,
    });
  }
  return { total: roundPrice(total), breakdown };
};

/**
 * Get package deal price and display format based on global currency context.
 * Supports both legacy flat price and new room-type structure.
 * @param {Object} priceObj - Deal price object (flat or { single, double, triple, quad })
 * @param {Object} currencyContext - { currency: 'USD'|'EUR', sign: ' $ '|' € ' }
 * @param {string} [roomType] - Optional room type when using room-type pricing
 * @returns {{ amount: number, sign: string, currencyCode: string, formatted: string }}
 */
export const getPackageDealPrice = (priceObj, currencyContext, roomType) => {
  if (!priceObj || typeof priceObj !== "object") {
    return { amount: 0, sign: " € ", currencyCode: "EUR", formatted: "0" };
  }

  const currencyCode = (currencyContext?.currency || "EUR").toUpperCase();
  const sign = currencyContext?.sign ?? " € ";

  if (hasRoomTypePricing(priceObj) && roomType) {
    const amount = getPriceForRoomType(priceObj, roomType, currencyContext);
    const formatted =
      amount % 1 === 0
        ? amount.toLocaleString()
        : amount.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
    return { amount, sign, currencyCode, formatted };
  }

  if (hasRoomTypePricing(priceObj)) {
    return getCheapestDealPrice(priceObj, currencyContext);
  }

  const priceMap = {
    USD: priceObj.usd,
    EUR: priceObj.eur,
    ANG: priceObj.ang,
    SRD: priceObj.srd,
  };
  const rawAmount =
    priceMap[currencyCode] ??
    priceObj.eur ??
    priceObj.usd ??
    priceObj.ang ??
    priceObj.srd ??
    0;
  const amount = roundPrice(rawAmount);
  const formatted =
    amount % 1 === 0
      ? amount.toLocaleString()
      : amount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

  return { amount, sign, currencyCode, formatted };
};
